import { getRoyaltyTrackingReport, getRoyaltyTrackingStats } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import moment from 'moment-timezone';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import TableTooltip from '@shared/Reports/TableTooltip';
import StatBox from '@shared/Reports/StatBox';
import RoyaltyFiltersBar from './RoyaltyFiltersBar';
import RoyaltyPrintViewTable from './RoyaltyPrintTable';
import RoyaltyQualificationBanner from './RoyaltyQualificationBanner';
import RoyaltyTable from './RoyaltyTable';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';

const RoyaltyReports = () => {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentQuarter = Math.floor(now.getMonth() / 3) + 1; // 1..4

	const [filters, setFilters] = useState({
		page: 1,
		year: { value: currentYear, label: currentYear.toString() },
		quarter: { value: currentQuarter, label: `Q${currentQuarter}` },
		room_id: [],
		timezone: null,
		sort: 'subscriber',
		dir: 'asc',
		export: null,
		student_name: 'first',
	});
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const navigate = useNavigate();
	const printRef = useRef();
	const [statsLoading, setStatsLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [statsData, setStatsData] = useState({});
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const exportTimeoutRef = useRef(null);

	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			timezone: user.data.school.timezone,
		}));
	}, [user]);

	const generateQueryParams = (filts, exp = null) => {
		return {
			page: filts.page,
			year: filts.year.value || currentYear,
			quarter: filts.quarter.value || currentQuarter,
			timezone: filts.timezone,
			room_id: filts.room_id?.length > 0 ? filts.room_id.map((r) => r.value) : null,
			sort: filts.sort,
			dir: filts.dir,
			export: exp,
		};
	};

	// (no-op helper removals)

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	useEffect(() => {
		const filtersChanged = Object.keys(filters).some((k) => k !== 'page' && filters[k] !== prevFilters.current[k]);
		if (filtersChanged) cache.current = {};
		prevFilters.current = filters;

		if (filters.year && filters.quarter) {
			setLoading(true);
			const queryParams = generateQueryParams(filters);
			const queryKey = JSON.stringify(queryParams);

			if (cache.current[queryKey]) {
				const cached = cache.current[queryKey];
				setRows(cached.data);
				setTotalResults(cached.total);
				setLoading(false);
				if (filtersChanged && cached.total >= 0) fetchStatsData(queryParams);
			} else {
				getRoyaltyTrackingReport(queryParams)
					.then((res) => {
						setRows(res.data.data || []);
						setTotalResults(res.data?.meta?.total || res.data?.total || 0);
						cache.current[queryKey] = { data: res.data.data || [], total: res.data?.meta?.total || 0 };
						if (filtersChanged) fetchStatsData(queryParams);
					})
					.catch(() => {
						dispatch(Actions.showMessage({ message: 'Failed to fetch royalty report', variant: 'error' }));
						setRows([]);
						setTotalResults(0);
						setStatsData({});
					})
					.finally(() => setLoading(false));
			}
		} else {
			setRows([]);
			setTotalResults(0);
			setStatsData({});
		}
	}, [filters]);

	const fetchStatsData = async (queryParams) => {
		setStatsLoading(true);
		try {
			const response = await getRoyaltyTrackingStats(queryParams);
			const stats = response.data || {};

			const totalFamilies = Number(stats.families ?? 0);
			const paidFamilies = Number(stats.paid_families ?? 0);

			const prevFamiliesRaw = stats.prev?.families;
			const prevPaidRaw = stats.prev?.paid_families;
			const prevRoyaltyRaw = stats.prev?.royalty;
			const prevQualificationRaw = stats.prev?.qualification;

			const prevTotalFamilies = prevFamiliesRaw != null ? Number(prevFamiliesRaw) : null;
			const prevPaidFamilies = prevPaidRaw != null ? Number(prevPaidRaw) : null;
			let prevRoyaltyAmount = prevRoyaltyRaw != null ? Number(prevRoyaltyRaw) : null;

			const safeDiff = (curr, prev) => (prev == null ? null : curr - prev);
			const safePctChange = (curr, prev) => {
				if (prev == null) return null;
				if (prev === 0) return curr > 0 ? Infinity : 0; // choose behavior for prev=0
				return ((curr - prev) / prev) * 100;
			};

			const paidPercentage =
				stats.qualification != null
					? Number(stats.qualification)
					: totalFamilies > 0
						? (paidFamilies / totalFamilies) * 100
						: 0;

			const prevPaidPercentage =
				prevQualificationRaw != null
					? Number(prevQualificationRaw)
					: prevTotalFamilies > 0
						? (prevPaidFamilies / prevTotalFamilies) * 100
						: 0;

			const prevTotalFamiliesChange = safeDiff(totalFamilies, prevTotalFamilies);
			const prevPaidFamiliesChange = safeDiff(paidFamilies, prevPaidFamilies);
			const paidPercentageChange = safeDiff(paidPercentage, prevPaidPercentage);

			let royaltyToDate = Number(stats.royalty ?? 0);

			// Projection (only when viewing the current quarter)
			let projectedRoyalty = null;
			let projectedRoyaltyChange = null;

			const previousQuarter = stats.prev?.quarter ?? null;
			const previousYear = stats.prev?.year ?? null;

			const isCurrentQuarter = filters.year.value === currentYear && filters.quarter.value === currentQuarter;

			if (isCurrentQuarter) {
				const getQuarterStartAndEnd = (year, quarter) => {
					const startMonth = (quarter - 1) * 3;
					const startDate = new Date(year, startMonth, 1);
					const endDate = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
					return { startDate, endDate };
				};

				const { startDate, endDate } = getQuarterStartAndEnd(filters.year.value, filters.quarter.value);
				const currentTime = Date.now();
				const totalQuarterMs = Math.max(1, endDate.getTime() - startDate.getTime());
				const elapsedQuarterMs = Math.min(Math.max(0, currentTime - startDate.getTime()), totalQuarterMs);
				const elapsedQuarterRatio = Math.min(1, Math.max(1e-6, elapsedQuarterMs / totalQuarterMs));

				projectedRoyalty = Math.max(royaltyToDate / elapsedQuarterRatio, royaltyToDate);
				projectedRoyaltyChange = safePctChange(projectedRoyalty, prevRoyaltyAmount);
			}

			if (paidPercentage < 50) {
				royaltyToDate = 0;
			}
			if (prevPaidPercentage < 50) {
				prevRoyaltyAmount = 0;
			}
			const royaltyChangeToDate = safePctChange(royaltyToDate, prevRoyaltyAmount);

			setStatsData({
				totalFamilies,
				prevTotalFamiliesChange,
				paidFamilies,
				prevPaidFamiliesChange,
				paidPercentage,
				paidPercentageChange,
				royaltyAmount: royaltyToDate, // keep if you use it elsewhere
				royaltyToDate, // explicit
				royaltyChangeToDate, // % vs previous quarter FINAL
				projectedRoyalty, // projection for current quarter
				projectedRoyaltyChange, // % vs previous quarter FINAL
				previousQuarter,
				previousYear,
			});
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to fetch royalty stats', variant: 'error' }));
			setStatsData({});
		} finally {
			setStatsLoading(false);
		}
	};

	const handleExport = async (type) => {
		if (filters.year.value && filters.quarter.value) {
			const queryParams = generateQueryParams(filters, type);
			try {
				setExportLoading(true);
				queryParams.uuid = uuidv4();
				window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
				await getRoyaltyTrackingReport(queryParams);
				exportTimeoutRef.current = setTimeout(() => {
					if (!exportLoading) return;
					setExportLoading(false);
					dispatch(
						Actions.showMessage({
							message: 'Export failed to generate. Please try again.',
							variant: 'error',
						})
					);
				}, 20000);
			} catch (error) {
				dispatch(Actions.showMessage({ message: 'Failed to export report', variant: 'error' }));
			}
		}
	};

	const handlePdfExport = async () => handleExport('pdf');

	const handlePrintClick = async () => {
		setExportType('print');
		if (filters.year.value && filters.quarter.value) {
			setPrintDataLoading(true);
			try {
				const queryParams = generateQueryParams(filters, 'print');
				const res = await getRoyaltyTrackingReport(queryParams);
				setPrintData(res.data.data || []);
			} catch (error) {
				console.error('Error fetching royalty report for print:', error);
			} finally {
				setPrintDataLoading(false);
			}
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const currency = (v) =>
		typeof v === 'number' ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : (v ?? '-');

	const columns = [
		{
			id: 'student',
			label: 'Student',
			render: (row) => {
				return (
					<div className="max-w-[200px] flex flex-col gap-8">
						{row?.students?.map((student, index) => {
							return (
								<div className="flex items-center gap-8" key={`${index}-${student.id}`}>
									<Avatar src={student?.photo} />
									<TableTooltip>
										<span className="font-bold truncate">
											{student ? `${student?.first_name} ${student?.last_name}` : '-'}
										</span>
									</TableTooltip>
								</div>
							);
						})}
					</div>
				);
			},
			printRender: (row) => {
				const names = row?.students
					?.map((student) => `${student?.first_name || ''} ${student?.last_name || ''}`)
					.filter((name) => name.trim());
				return names?.join(', ') || '-';
			},
			widthClass: 'min-w-200',
		},
		{
			id: 'room',
			label: 'Room',
			render: (row) => {
				return (
					<div className="flex flex-col gap-8">
						{row?.rooms?.map((room, index) => (
							<div className="flex items-center gap-8" key={`${index}-${room.id}`}>
								<Avatar src={room?.photo} />
								<TableTooltip>
									<span className="truncate">{room?.name || '-'}</span>
								</TableTooltip>
							</div>
						))}
					</div>
				);
			},
			printRender: (row) => {
				const rooms = row?.rooms?.map((room) => room?.name).filter((name) => name);
				return rooms?.join(', ') || '-';
			},
			widthClass: 'min-w-200',
		},
		{
			id: 'subscriber',
			label: 'Subscriber',
			render: (row) => (
				<div className="flex items-center gap-16">
					<Avatar src={row?.subscriber?.thumb || row?.subscriber?.photo} />
					<div className="flex flex-col truncate">
						<TableTooltip content={`${row?.subscriber?.first_name} ${row?.subscriber?.last_name}`}>
							<span className="font-bold truncate">
								{row?.subscriber?.first_name} {row?.subscriber?.last_name}
							</span>
						</TableTooltip>
						<span>{row?.subscriber?.relationship || 'Contact'}</span>
					</div>
				</div>
			),
			printRender: (row) => `${row?.subscriber?.first_name || ''} ${row?.subscriber?.last_name || ''}`,
			widthClass: 'min-w-200',
		},
		{
			id: 'email',
			label: 'Email',
			render: (row) => (
				<TableTooltip title={row?.subscriber?.email}>
					<div className="truncate">{row?.subscriber?.email || '-'}</div>
				</TableTooltip>
			),
			printRender: (row) => row?.subscriber?.email || '-',
			widthClass: 'min-w-200',
		},
		{
			id: 'package',
			label: 'Plan',
			render: (row) => {
				const display = row?.package || '-';
				return <span className="px-8 py-2 rounded bg-blue-50 text-blue-700">{display}</span>;
			},
			printRender: (row) => (row?.package || '-'),
			widthClass: 'min-w-96',
		},
		{
			id: 'amount',
			label: 'Total\nPayments',
			widthClass: 'min-w-128',
			render: (row) => currency(row?.total_payments),
			printRender: (row) => currency(row?.total_payments),
		},
		{
			id: 'royalty',
			label: 'Total\nRoyalty Amount',
			widthClass: 'min-w-128',
			render: (row) => currency(row?.total_royalty || 0),
			printRender: (row) => currency(row?.total_royalty || 0),
		},
		{
			id: 'since',
			label: 'Subscribed\nSince',
			render: (row) => (row?.subscription_date ? moment(row.subscription_date).format('MM/DD/YY') : '—'),
			widthClass: 'min-w-96',
			printRender: (row) => (row?.subscription_date ? moment(row.subscription_date).format('MM/DD/YY') : '—'),
		},
		{
			id: 'until',
			label: 'Renews\nOn',
			render: (row) => (row?.ending_date ? moment(row.ending_date).format('MM/DD/YY') : '—'),
			widthClass: 'min-w-96',
			printRender: (row) => (row?.ending_date ? moment(row.ending_date).format('MM/DD/YY') : '—'),
		},
	];

	const currentQuarterSelected = filters?.year?.value === currentYear && filters?.quarter?.value === currentQuarter;
	const needPct = Math.max(0, 50 - (statsData?.paidPercentage || 0));

	const formatPercentChange = (change) => {
		if (change == null) return null;
		if (change === Infinity) return '∞';
		const n = Number(change);
		if (Number.isNaN(n)) return null;
		const fixed = n.toFixed(1);
		return n > 0 ? `+${fixed}` : fixed;
	};

	return (
		<div>
			<ReportsHeader
				exportLoading={exportLoading}
				exportsDisabled={!rows?.length}
				onBack={() => navigate(-1)}
				onDownloadPDF={handlePdfExport}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="Track royalty qualification and projected earnings for your school."
				title="Royalty Performance"
			/>
			<RoyaltyFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar
				filters={filters}
				setFilters={updateFilters}
				hideFilters={['timezone', 'sort', 'dir', 'page', 'student_name']}
			/>
			<div className="bg-white">
				{filters?.year?.value && filters?.quarter?.value ? (
					<div className="px-32 py-48">
						{!statsLoading && statsData?.totalFamilies != null && (
							<RoyaltyQualificationBanner
								currency={currency}
								paidFamilies={statsData.paidFamilies}
								paidPercentage={statsData.paidPercentage}
								projectedRoyalty={statsData.projectedRoyalty}
								royaltyToDate={statsData.royaltyToDate}
								showProjection={currentQuarterSelected}
								totalFamilies={statsData.totalFamilies}
							/>
						)}
						<div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-16">
							<StatBox
								data={{
									total: statsLoading ? 0 : (statsData.totalFamilies ?? 0),
									dateRange: null,
								}}
								loading={statsLoading || loading}
								subtext={`${statsData?.prevTotalFamiliesChange > 0 ? '+' : ''}${statsData?.prevTotalFamiliesChange} (vs Q${statsData?.previousQuarter} ${statsData?.previousYear})`}
								title="Total Families"
								type="totalStudent"
							/>
							<StatBox
								data={{
									total: statsLoading ? 0 : (statsData?.paidFamilies ?? 0),
									dateRange: null,
								}}
								subtext={`${statsData?.prevPaidFamiliesChange > 0 ? '+' : ''}${statsData?.prevPaidFamiliesChange} (vs Q${statsData?.previousQuarter} ${statsData?.previousYear})`}
								loading={statsLoading || loading}
								title="Families on Paid Plan"
								type="totalStudent"
							/>
							<StatBox
								data={{
									total: `${statsData?.paidPercentage?.toFixed(1) ?? 0}%`,
									dateRange: null,
								}}
								loading={statsLoading || loading}
								title={
									statsData?.paidPercentage >= 50
										? 'Qualification status'
										: `Qualification Status: ${needPct.toFixed(1)}% more needed`
								}
								subtext={`${formatPercentChange(statsData?.paidPercentageChange)}% (vs Q${statsData?.previousQuarter} ${statsData?.previousYear})`}
								type="qualification"
								trend={statsData?.paidPercentage >= 50 ? 'pass' : 'fail'}
							/>
							{currentQuarterSelected ? (
								<>
									<StatBox
										data={{
											total: currency(statsData?.royaltyToDate ?? 0),
											dateRange: null,
										}}
										loading={statsLoading || loading}
										title="Royalty Amount to Date"
										subtext={`${formatPercentChange(statsData?.royaltyChangeToDate)}% (vs Q${statsData?.previousQuarter ?? '—'} ${statsData?.previousYear ?? '—'})`}
										type="dailyTime"
										trend={
											statsData?.royaltyChangeToDate == null
												? undefined
												: statsData?.royaltyChangeToDate > 0
													? 'positive'
													: statsData?.royaltyChangeToDate < 0
														? 'negative'
														: 'neutral'
										}
									/>
									<StatBox
										data={{ total: currency(statsData?.projectedRoyalty ?? 0), dateRange: null }}
										loading={statsLoading || loading}
										title="Projected Quarterly Royalty"
										type="dailyTime"
										subtext={`${formatPercentChange(statsData?.projectedRoyaltyChange)}% (vs Q${statsData?.previousQuarter ?? '—'} ${statsData?.previousYear ?? '—'})`}
										trend={
											statsData?.projectedRoyaltyChange == null
												? undefined
												: statsData?.projectedRoyaltyChange > 0
													? 'positive'
													: statsData?.projectedRoyaltyChange < 0
														? 'negative'
														: 'neutral'
										}
									/>
								</>
							) : (
								<StatBox
									data={{
										total: currency(statsData?.royaltyAmount ?? 0),
										dateRange: null,
									}}
									loading={statsLoading || loading}
									title="Royalty Amount"
									trend={
										statsData?.royaltyChangeToDate == null
											? undefined
											: statsData?.royaltyChangeToDate > 0
												? 'positive'
												: statsData?.royaltyChangeToDate < 0
													? 'negative'
													: 'neutral'
									}
									subtext={`${formatPercentChange(statsData?.royaltyChangeToDate)}% (vs Q${statsData?.previousQuarter ?? '—'} ${statsData?.previousYear ?? '—'})`}
									type="dailyTime"
								/>
							)}
						</div>
					</div>
				) : null}
				<div>
					<RoyaltyTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="">
					<RoyaltyPrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Royalty Tracking Report"
						user={user}
					/>
				</div>
			</div>
		</div>
	);
};

export default RoyaltyReports;
